/**
 * Roster Intel API Client
 * Shared types and fetch function for Roster Intel Panel (web + mobile).
 *
 * Data source: MySportsFeeds v2.1 (roster, injuries, projected lineups)
 * + BDL injury opportunity analysis (stat redistribution when players are out)
 */

import type { HttpClient } from './httpClient';

// =============================================================================
// Types
// =============================================================================

export interface RosterIntelOpportunity {
  beneficiaryName: string;
  beneficiaryId: number;
  statDifferentials: Partial<
    Record<'pts' | 'reb' | 'ast' | 'stl' | 'blk' | 'fg3m' | 'turnover' | 'min', number>
  >;
  sampleSize: number;
  confidence: 'high' | 'medium' | 'low';
}

/** Injury status shared across backend and frontend */
export type InjuryStatus = 'Out' | 'Out For Season' | 'Questionable' | 'Doubtful' | 'Probable';

export interface RosterIntelPlayer {
  playerId: number;
  playerName: string;
  position: string; // MSF: "PG" | "SG" | "SF" | "PF" | "C"; BDL roster: "G" | "F" | "C" | "G-F" | "F-C" or ""
  mpg: number | null;
  jerseyNumber: number | null;
  injuryStatus?: InjuryStatus;
  injuryDescription?: string;
  gamesMissed?: number; // Consecutive recent DNP games
  /** Beneficiaries that absorb this player's minutes when they are OUT */
  opportunities?: RosterIntelOpportunity[];
}

export interface RosterIntelLineupSlot {
  slot: string; // "Starter1" … "Bench8"
  isStarter: boolean;
  player: RosterIntelPlayer;
}

/** Paired starter matchup: one team player vs their positional opponent */
export interface StarterMatchup {
  slotNumber: number; // 1-5
  teamPlayer: RosterIntelPlayer;
  opponentPlayer: RosterIntelPlayer | null;
  /** Note when a projected starter was replaced (e.g., traded mid-season) */
  replacedNote?: string;
}

export interface RosterIntelResponse {
  teamAbbr: string;
  teamName: string;
  gameDate: string; // YYYYMMDD
  /** null when no game today or lineup not yet published */
  projectedLineup: RosterIntelLineupSlot[] | null;
  /** Paired starter matchups (team vs opponent). null when no opponent or no lineup */
  starterMatchups: StarterMatchup[] | null;
  /** Opponent team abbreviation (echoed from request) */
  opponentAbbr?: string;
  /** Opponent team full name */
  opponentName?: string;
  /** Full roster sorted by MPG desc */
  roster: RosterIntelPlayer[];
  /** Set when the roster came from BDL: which season its minutes are from ('previous' before a season's first game) */
  mpgSeason?: 'current' | 'previous';
}

// MPG thresholds for roster tier display
export const STARTER_MPG = 25;
export const PRIMARY_BENCH_MPG = 15;
export const SECONDARY_BENCH_MPG = 8;

export const ROSTER_UNAVAILABLE_TEXT = 'Roster data not available for this team';

export function mpgNoteText(mpgSeason: RosterIntelResponse['mpgSeason']): string {
  return mpgSeason === 'previous'
    ? "All MPG values are last season's averages"
    : 'All MPG values are season averages';
}

// =============================================================================
// Factory
// =============================================================================

export interface TeamNewsItem {
  id: string;
  headline: string;
  body: string;
  publishedAt: string;
  playerName: string | null;
  teamAbbr: string | null;
}

export interface TeamNewsResponse {
  teamAbbr: string;
  news: TeamNewsItem[];
}

export function createTeamsApi(client: HttpClient) {
  return {
    /**
     * Fetch Roster Intel for a given team + date.
     * @param teamAbbr - e.g. "BOS", "LAL"
     * @param gameDate - YYYYMMDD format (defaults to today on server if omitted)
     */
    async getRosterIntel(
      teamAbbr: string,
      options?: {
        gameDate?: string;
        opponentAbbr?: string;
        skipOpportunities?: boolean;
        leagueKey?: string;
      },
    ): Promise<RosterIntelResponse> {
      const params: Record<string, string> = {};
      if (options?.gameDate) params.gameDate = options.gameDate;
      if (options?.opponentAbbr) params.opponentAbbr = options.opponentAbbr;
      if (options?.skipOpportunities) params.skipOpportunities = 'true';
      if (options?.leagueKey) params.leagueKey = options.leagueKey;
      return client.get<RosterIntelResponse>(`/api/teams/${teamAbbr}/intel`, params);
    },

    /**
     * Fetch latest news for a team (requires MSF News addon).
     */
    async getTeamNews(teamAbbr: string): Promise<TeamNewsResponse> {
      return client.get<TeamNewsResponse>(`/api/teams/${teamAbbr}/news`);
    },
  };
}

export type TeamsApi = ReturnType<typeof createTeamsApi>;
